<template>
  <picker
    :id="id"
    label="color"
    :text="value"
    :skeleton="skeleton"
    class="picker--color"
    data-cy-color-picker
  >
    <p class="picker__label text-big text-secondary mb-0-8">
      <span v-if="withLabel">{{ $t('label.color') }}</span>
      <span>{{ hoveredColor || selectColorLabel }}</span>
    </p>
    <div
      v-if="skeleton"
      class="picker__elements d-flex flex-wrap justify-center"
    >
      <div
        v-for="(color, index) in colors"
        :key="index"
        class="picker__element d-flex flex-wrap align-center"
      >
        <picker-item name="color" :value="`${index}`" skeleton />
      </div>
    </div>
    <div v-else class="picker__elements d-flex flex-wrap justify-center">
      <div
        v-for="color in colorsWithBreaks"
        :key="color.color"
        class="picker__element d-flex flex-wrap align-center"
        :class="{ break: color.break }"
        :data-cy-color-picker-element="value === color.value ? 'current' : ''"
      >
        <picker-item
          v-if="!color.break"
          name="color"
          :value="color.value"
          :label="color.label"
          :active="value === color.value"
          :bg="color.image.url"
          :has-notif="color.isNew || color.isLeavingSoon"
          @input="selectColor(color)"
          @mouseenter="hoverColor(color)"
          @mouseleave="leaveColor(color)"
        />
      </div>
    </div>

    <transition name="fade-from-top">
      <div
        v-if="showIsLeavingSoon || showIsNew"
        class="picker__notif text-small text-md-base text-left mt-0-8"
      >
        <p class="mb-0-2 w-600">
          {{
            $i18n.t(`product.color.${showIsNew ? 'new' : 'leaving_soon'}.title`)
          }}
        </p>
        <p v-if="showIsLeavingSoon && daysLeft === -1" class="mb-0">
          {{ $i18n.t('product.color.leaving_soon.ended') }}
        </p>
        <p
          v-else
          class="mb-0"
          :class="{
            'd-none': $i18n.locale === 'en-US' && showIsNew,
          }"
        >
          {{
            $i18n.t(
              `product.color.${showIsNew ? 'new' : 'leaving_soon'}.desc`,
              {
                left:
                  daysLeft !== null
                    ? $i18n.tc(
                        'product.color.leaving_soon.days_left',
                        daysLeft,
                        {
                          count: daysLeft,
                        }
                      )
                    : $i18n.t('product.color.leaving_soon.some_days'),
                product: productDocument.title,
                colour: selectColorLabel,
              }
            )
          }}
        </p>
      </div>
    </transition>
  </picker>
</template>

<script>
import { differenceInDays, isPast } from 'date-fns'
import Picker from '@/components/picker/Picker'
import PickerItem from '@/components/picker/PickerItem'

export default {
  name: 'ColorPicker',
  components: {
    Picker,
    PickerItem,
  },
  props: {
    value: { type: String, required: true },
    /**
     * Liste des couleurs
     */
    colors: {
      type: Array,
      required: true,
    },
    /**
     * identifiant du color picker
     */
    id: {
      type: String,
      default: 'colorPicker',
    },
    /**
     * Produit sanity
     */
    productDocument: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Affiche le label du color picker
     */
    withLabel: {
      type: Boolean,
      default: false,
    },
    /**
     * Le composant est affiché en squelette de chargement
     */
    skeleton: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    hoveredColor: null,
  }),
  computed: {
    selectColorLabel() {
      if (
        this.value &&
        this.colors.some((color) => color.value === this.value)
      ) {
        return this.colors.find((color) => color.value === this.value).label
      }

      return ''
    },
    showIsLeavingSoon() {
      return (
        this.value &&
        this.colors.some(
          (color) => color.value === this.value && color.isLeavingSoon
        )
      )
    },
    showIsNew() {
      return (
        this.value &&
        this.colors.some((color) => color.value === this.value && color.isNew)
      )
    },
    maxPerLine() {
      if (this.colors.length > 8) {
        return Math.ceil(this.colors.length / 2)
      }

      return 8
    },
    numberOfLine() {
      return Math.ceil(this.colors.length / this.maxPerLine)
    },
    colorsWithBreaks() {
      let arr = []

      for (let i = 0; i < this.numberOfLine; i++) {
        arr = arr.concat(
          this.colors.slice(this.maxPerLine * i, this.maxPerLine * (i + 1))
        )

        arr.push({
          color: `break-${i}`,
          break: true,
        })
      }

      return arr
    },
    daysLeft() {
      if (this.showIsLeavingSoon) {
        if (this.productDocument.current_batch_date_end) {
          if (isPast(this.productDocument.current_batch_date_end)) {
            return -1
          }

          return differenceInDays(
            this.productDocument.current_batch_date_end,
            new Date()
          )
        }

        return 0
      }
      return null
    },
  },
  methods: {
    selectColor(color) {
      this.$emit('input', color.value)
    },
    hoverColor(color) {
      this.hoveredColor = color.label
    },
    leaveColor() {
      this.hoveredColor = null
    },
  },
}
</script>

<style lang="scss">
.picker.picker--color {
  margin-bottom: var(--spacing);

  .picker__elements {
    margin: 0 rem(-5px);
  }

  .picker__element {
    margin: 0 rem(5px) rem($spacing * 0.6);

    &.break {
      display: block;
      height: 0;
      flex: 1 0 100%;
      margin: 0;
    }
  }

  .picker__element__color {
    white-space: nowrap;
    will-change: width opacity;
  }

  .picker-item {
    margin: 0;
  }
}

.picker__notif {
  background-color: var(--secondary-color-5);
  padding: calc(var(--spacing) * 0.8) calc(var(--spacing));

  @include mq($from: tablet) {
    padding: calc(var(--spacing)) calc(var(--spacing) * 1.4);
  }
}
</style>
